import { useEffect, useState } from "react";
import type { DuplicateCandidate, Patient } from "@dentacore/shared-types";
import { PHONE_ALREADY_USED } from "@dentacore/shared-types";

import { useAuth } from "../auth/auth-context";
import { ApiError } from "../api/client";
import { createPatient, possibleDuplicates } from "../api/patients";
import { DuplicateWarning } from "./duplicates";
import { ConfirmDialog } from "../ui/confirm-dialog";
import { Modal } from "../ui/modal";
import { PhoneInput } from "../ui/phone-input";
import { InsurerField } from "./insurer-field";
import styles from "./new-patient-form.module.css";
import { RequiredMark } from "../ui/required-mark";

/**
 * "Create a file" — the mockup's `newPatient` panel, **as a dialog** like New
 * appointment (2026-10-03), rather than a page that replaced the list. The list
 * or the file it was opened from stays underneath, and closing it — Cancel, the
 * backdrop or Escape — lands back there.
 *
 * **One deliberate departure from the mockup's fields:** it asks for a single
 * "Full name". The record stores `firstName`/`lastName` separately (every
 * other screen renders them that way), and splitting a typed full name on the
 * first space silently mangles anyone with a middle name or a two-word
 * surname — so this form asks for both directly instead of guessing.
 *
 * **Age becomes Date of birth.** Working plan / `DATA-MODEL.md` §2 already
 * settled this divergence: the record stores `dateOfBirth` and derives age on
 * every read, because a stored age is wrong the day after a birthday.
 */
export function NewPatientForm({
  onCreated,
  onCancel,
}: {
  onCreated: (patient: Patient) => void;
  onCancel: () => void;
}): React.JSX.Element {
  const { user } = useAuth();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [phone, setPhone] = useState("");
  const [insurer, setInsurer] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * The collision being asked about, or null when there is none.
   *
   * **D2's guard is untouched** — a number belongs to one patient unless
   * somebody says otherwise, and this is only ever raised by a real collision,
   * never offered up front. What changed (Anthony's QA, item 18) is that the
   * escape hatch is a confirm rather than a checkbox and a second save.
   *
   * It holds the *other* patient rather than a boolean, because the question
   * only makes sense with their name in it: "somebody already has this number"
   * is not a question anybody can answer, and "Rita Haddad already has it" is.
   */
  const [clash, setClash] = useState<{
    patientName: string | null;
    fileNumber: string | null;
  } | null>(null);

  /**
   * STORY-122 (SA3). The file already exists by the time this is set — it is
   * an acknowledgement, not a refusal, which is what tells it apart from
   * `clash` above.
   */
  const [createdPendingAck, setCreatedPendingAck] = useState<Patient | null>(null);

  /**
   * **Possibly already here** (PP-D14, OQ-D6): once a name and a date of birth
   * are typed, files with the same date and a close name are offered. A
   * warning, never a refusal — twins exist.
   */
  const [lookalikes, setLookalikes] = useState<readonly DuplicateCandidate[]>([]);
  useEffect(() => {
    setLookalikes([]);
    if (firstName.trim() === "" || lastName.trim() === "" || dateOfBirth === "") return;
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      possibleDuplicates(
        { firstName: firstName.trim(), lastName: lastName.trim(), dateOfBirth },
        controller.signal,
      )
        .then(setLookalikes)
        .catch(() => {
          /* No warning; the file still saves. */
        });
    }, 300);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [firstName, lastName, dateOfBirth]);

  const canSave = firstName.trim() !== "" && lastName.trim() !== "" && phone.trim() !== "" && !busy;

  /**
   * Save, and on a phone collision ask rather than refuse (Anthony's QA, #18).
   *
   * ── What changed, and what deliberately did not ─────────────────────────
   *
   * D2's guard is untouched: a number belongs to one patient unless somebody
   * says otherwise, the question is only ever raised by a real collision, and
   * nothing is pre-tickable. What changed is the shape of the answer.
   *
   * It used to be a checkbox the form revealed on refusal, so saving a
   * genuinely shared number cost **two saves**: type, be refused, find the
   * tick, save again. Anthony's report was to remove the tick. This removes it
   * and keeps the guard, by asking the question at the moment it arises and
   * taking the answer as the second half of the same save.
   *
   * `shared` is a parameter rather than state because it is an answer to one
   * attempt, not a setting: a second collision on a different number has to ask
   * again, and a flag left set from last time would quietly say yes for it.
   */
  const save = (shared: boolean): void => {
    if (!canSave) return;

    setBusy(true);
    setError(null);

    createPatient({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      phoneE164: phone.trim(),
      ...(dateOfBirth === "" ? {} : { dateOfBirth }),
      ...(insurer.trim() === "" ? {} : { insurerName: insurer.trim() }),
      ...(shared ? { sharesHouseholdNumber: true } : {}),
    })
      .then((patient) => {
        setClash(null);
        if (patient.staffCollision == null && patient.phoneWarning == null) onCreated(patient);
        else setCreatedPendingAck(patient);
      })
      .catch((cause: unknown) => {
        // Everything typed stays exactly where it is — the dialog offers the one
        // action that resolves the refusal, rather than starting over.
        if (cause instanceof ApiError && cause.code === PHONE_ALREADY_USED) {
          const details = cause.details ?? {};
          const name = details["patientName"];
          const file = details["fileNumber"];

          setClash({
            patientName: typeof name === "string" ? name : null,
            // Already padded by the server, which is the one place that knows
            // how a file number is written (§6.5).
            fileNumber: typeof file === "string" ? file : null,
          });
          return;
        }

        setError(cause instanceof Error ? cause.message : "Could not create this file.");
      })
      .finally(() => setBusy(false));
  };

  return (
    <Modal label="New patient" testId="new-patient-form" onClose={onCancel}>
      <div>
        <h2 className={styles.title}>New patient</h2>
        <p className={styles.subtitle}>
          Only the essentials — the rest is filled at the first examination.
        </p>
      </div>

      {error !== null && (
        <p className={styles.error} data-testid="new-patient-error">
          {error}
        </p>
      )}

      <div className={styles.fields}>
        <div className={styles.row}>
          <label className={styles.field}>
            <span className={styles.label}>
              First name
              <RequiredMark />
            </span>
            <input
              className={styles.input}
              value={firstName}
              onChange={(event) => setFirstName(event.target.value)}
              placeholder="Dina"
              data-testid="new-patient-first-name"
            />
          </label>
          <label className={styles.field}>
            <span className={styles.label}>
              Last name
              <RequiredMark />
            </span>
            <input
              className={styles.input}
              value={lastName}
              onChange={(event) => setLastName(event.target.value)}
              placeholder="Fares"
              data-testid="new-patient-last-name"
            />
          </label>
        </div>

        <div className={styles.row}>
          <label className={styles.field}>
            <span className={styles.label}>Date of birth</span>
            <input
              className={styles.input}
              type="date"
              value={dateOfBirth}
              onChange={(event) => setDateOfBirth(event.target.value)}
              data-testid="new-patient-dob"
            />
          </label>
          <InsurerField
            value={insurer}
            onChange={setInsurer}
            testId="new-patient-insurer"
            className={styles.field}
          />
        </div>

        <PhoneInput
          value={phone}
          onChange={(next) => {
            setPhone(next);
            // A different number is a different question — the answer given for
            // the last one must not be carried onto a number that never
            // collided.
            if (clash !== null) setClash(null);
          }}
          defaultCountry={user?.clinicDefaultCountry}
          testIdPrefix="new-patient-phone"
          required
          invalid={clash !== null}
        />
      </div>

      <DuplicateWarning candidates={lookalikes} />

      <div className={styles.actions}>
        <button
          type="button"
          className={styles.primary}
          disabled={!canSave}
          onClick={() => save(false)}
          data-testid="new-patient-save"
        >
          Create file
        </button>
        <button
          type="button"
          className={styles.secondary}
          disabled={busy}
          onClick={onCancel}
          data-testid="new-patient-cancel"
        >
          Cancel
        </button>
      </div>

      {clash !== null && (
        <ConfirmDialog
          title="That number is already on a file"
          confirmLabel="Add anyway"
          busy={busy}
          onCancel={() => setClash(null)}
          onConfirm={() => save(true)}
          testId="new-patient-share-number"
        >
          <p>
            {clash.patientName === null
              ? "Another patient already has this number."
              : `${clash.patientName}${
                  clash.fileNumber === null ? "" : ` (file ${clash.fileNumber})`
                } already has this number.`}
          </p>
          <p>
            Open their file instead if this is the same person. Add anyway if two people genuinely
            share it &mdash; a couple, or a parent and child.
          </p>
        </ConfirmDialog>
      )}

      {/*
        STORY-122 (SA3). The file is already created by the time this shows —
        an acknowledgement, not a refusal, which is why there is one button
        rather than a confirm/cancel pair.
      */}
      {createdPendingAck !== null && (
        <ConfirmDialog
          title="File created"
          confirmLabel="Continue"
          busy={false}
          onCancel={() => onCreated(createdPendingAck)}
          onConfirm={() => onCreated(createdPendingAck)}
          testId="new-patient-staff-collision"
        >
          {createdPendingAck.staffCollision != null && (
            <p>
              This number is also {createdPendingAck.staffCollision.name}&rsquo;s WhatsApp assistant
              number. Worth checking it belongs to the right person.
            </p>
          )}
          {/* Saved, with a warning (PP-D15): the right length, not a number for its country. */}
          {createdPendingAck.phoneWarning != null && (
            <p data-testid="new-patient-phone-warning">{createdPendingAck.phoneWarning}</p>
          )}
        </ConfirmDialog>
      )}
    </Modal>
  );
}
